import { useState, useRef, useEffect, useCallback } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Printer, X, Mail, Loader2, WifiOff, Clock, Zap } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { printReceiptSilent, isQzTrayAvailable, getDefaultPrinter } from "@/utils/qzPrint";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const RECEIPT_IDLE_TIMEOUT_MS = 5 * 1000; // Auto-close after 5 seconds of inactivity

// Default company info (used as fallback ONLY when the /settings/company call fails.
// Deliberately blank — better an empty line on the receipt than a misleading address.)
const DEFAULT_COMPANY_INFO = {
  name: "BOSVELD BOU & HARDEWARE",
  shortName: "BBH",
  logo: "https://customer-assets.emergentagent.com/job_smartretail-hub/artifacts/0kbrcukq_logo%20klein.jpg",
  address: "",
  city: "",
  phone: "",
  vatNumber: "",
  email: ""
};

const COMPANY_CACHE_KEY = "bbh.companySettings.v1";

function loadCachedCompanyInfo() {
  try {
    const raw = localStorage.getItem(COMPANY_CACHE_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch { return null; }
}

export const ReceiptModal = ({ open, onClose, transaction, vatEnabled: vatEnabledProp, autoPrint = false, copies = 1 }) => {
  const [receiptFormat, setReceiptFormat] = useState("thermal"); // "thermal" or "a4"
  // Seed from localStorage if we've ever fetched company settings before — this
  // eliminates the auto-print race that was printing the old default address.
  const [companyInfo, setCompanyInfo] = useState(() => loadCachedCompanyInfo() || DEFAULT_COMPANY_INFO);
  const [companyInfoLoaded, setCompanyInfoLoaded] = useState(() => loadCachedCompanyInfo() !== null);
  // The explicit prop still wins (some callers force-disable VAT for internal
  // vouchers), but by default we take the value from companyInfo which is
  // driven straight from Company Settings — so as soon as the owner ticks
  // "VAT registered" + enters a VAT number, every till slip becomes a proper
  // tax invoice automatically.
  const vatEnabled = typeof vatEnabledProp === "boolean" ? vatEnabledProp : !!companyInfo.vatEnabled;
  const [showEmailDialog, setShowEmailDialog] = useState(false);
  const [emailAddress, setEmailAddress] = useState("");
  const [sendingEmail, setSendingEmail] = useState(false);
  const [hasPrinted, setHasPrinted] = useState(false);
  const [qzAvailable, setQzAvailable] = useState(false);
  // Live, refreshable copy of the transaction. We re-fetch on open for account
  // sales so the PAID/NOT PAID stamp reflects the latest amount_paid (set by
  // account-receipt allocations / reconciliations).
  const [liveTransaction, setLiveTransaction] = useState(transaction);
  const autoCloseTimerRef = useRef(null);
  const printRef = useRef();
  // Keep a stable ref to onClose so the auto-print effect doesn't re-run when the
  // parent re-renders and passes a new inline function reference
  const onCloseRef = useRef(onClose);
  useEffect(() => { onCloseRef.current = onClose; }, [onClose]);

  const clearAutoCloseTimer = useCallback(() => {
    if (autoCloseTimerRef.current) {
      clearTimeout(autoCloseTimerRef.current);
      autoCloseTimerRef.current = null;
    }
  }, []);

  const restartAutoCloseTimer = useCallback(() => {
    if (!open) return;
    clearAutoCloseTimer();
    autoCloseTimerRef.current = setTimeout(() => {
      onCloseRef.current();
    }, RECEIPT_IDLE_TIMEOUT_MS);
  }, [open, clearAutoCloseTimer]);

  const handleModalInteraction = useCallback(() => {
    restartAutoCloseTimer();
  }, [restartAutoCloseTimer]);

  // Check QZ Tray availability on mount
  useEffect(() => {
    const checkQz = async () => {
      const available = await isQzTrayAvailable();
      setQzAvailable(available);
      if (available) {
        console.log('QZ Tray is available for silent printing');
      }
    };
    checkQz();
  }, []);

  // Fetch company settings when modal opens
  useEffect(() => {
    const fetchCompanySettings = async () => {
      try {
        const response = await axios.get(`${API}/settings/company`);
        const settings = response.data;
        const next = {
          name: settings.company_name || DEFAULT_COMPANY_INFO.name,
          shortName: settings.short_name || DEFAULT_COMPANY_INFO.shortName,
          logo: settings.logo_url || DEFAULT_COMPANY_INFO.logo,
          address: settings.address_line1 || DEFAULT_COMPANY_INFO.address,
          addressLine2: settings.address_line2 || "",
          city: [settings.city, settings.province].filter(Boolean).join(", ") || DEFAULT_COMPANY_INFO.city,
          postalCode: settings.postal_code || "",
          phone: settings.phone || DEFAULT_COMPANY_INFO.phone,
          // Store Number (kept in DB as `fax` for backwards compat)
          storeNumber: settings.fax || "",
          // Now that Bosveld is VAT-registered we ALWAYS surface the VAT number
          // on the receipt when it's present (previously we hid it if
          // vat_enabled was false — but the VAT number is a legal requirement
          // once a business is registered, so the moment it's saved to
          // settings we show it).
          vatNumber: settings.vat_number ? `VAT: ${settings.vat_number}` : "",
          registrationNumber: settings.registration_number ? `Reg: ${settings.registration_number}` : "",
          // Auto-detect vatEnabled from company settings so every caller of
          // ReceiptModal gets the correct tax-invoice treatment without having
          // to remember to pass the prop. Strict boolean check — a missing/
          // undefined vat_enabled must NOT be treated as truthy.
          vatEnabled: settings.vat_enabled === true && !!settings.vat_number,
          email: settings.email || DEFAULT_COMPANY_INFO.email,
          footerLine1: settings.receipt_footer_line1 || "",
          footerLine2: settings.receipt_footer_line2 || "",
          footerLine3: settings.receipt_footer_line3 || "",
          footerLine4: settings.receipt_footer_line4 || "",
          footerLine5: settings.receipt_footer_line5 || ""
        };
        setCompanyInfo(next);
        setCompanyInfoLoaded(true);
        try { localStorage.setItem(COMPANY_CACHE_KEY, JSON.stringify(next)); } catch { /* quota / disabled */ }
      } catch (error) {
        console.error("Error fetching company settings:", error);
        // Keep whatever we have (cache or defaults). Mark as loaded only if we
        // had a cached value — otherwise auto-print stays blocked so we never
        // print the blank placeholder.
        if (loadCachedCompanyInfo()) setCompanyInfoLoaded(true);
      }
    };

    if (open) {
      fetchCompanySettings();
      // Pre-fill email if customer has email
      if (transaction?.customer_email) {
        setEmailAddress(transaction.customer_email);
      }
      // Reset print state when modal opens
      setHasPrinted(false);
    }
  }, [open, transaction]);

  // Keep liveTransaction in sync with the prop, then re-fetch the latest
  // amount_paid for the sale so the PAID/NOT PAID stamp is never stale.
  useEffect(() => {
    setLiveTransaction(transaction);
    if (!open || !transaction?.transaction_number) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(
          `${API}/sales/${encodeURIComponent(transaction.transaction_number)}`
        );
        if (cancelled || !res.data) return;
        // Merge: prefer freshly-fetched fields but keep anything the caller
        // already provided (e.g. local UI hints).
        setLiveTransaction((prev) => ({ ...(prev || {}), ...res.data }));
      } catch (_) {
        // Best-effort refresh — fall back silently to the prop.
      }
    })();
    return () => { cancelled = true; };
  }, [open, transaction]);

  // Auto-close receipt modal after inactivity
  useEffect(() => {
    if (!open) {
      clearAutoCloseTimer();
      return;
    }

    restartAutoCloseTimer();

    return () => {
      clearAutoCloseTimer();
    };
  }, [open, restartAutoCloseTimer, clearAutoCloseTimer]);

  // Auto-print effect - triggers print automatically when autoPrint is true
  // Uses QZ Tray for silent printing if available, otherwise falls back to browser print.
  // CRITICAL: gated on `companyInfoLoaded` to prevent the race where the print job
  // fired before /settings/company resolved, baking the default address into the receipt.
  useEffect(() => {
    if (open && autoPrint && transaction && companyInfo && companyInfoLoaded && !hasPrinted) {
      const printTimer = setTimeout(async () => {
        const printContent = printRef.current;
        if (!printContent) return;
        
        const receiptHtml = printContent.innerHTML;
        
        // Try QZ Tray first for silent printing
        if (qzAvailable && getDefaultPrinter()) {
          try {
            // Print requested number of copies silently with QZ Tray
            // (1 = paper-saving default; 2 = triggered by yard/outside stock in the cart)
            const result = await printReceiptSilent(receiptHtml, copies);
            
            if (result.success) {
              toast.success(`Receipt printed to ${result.printer}${copies > 1 ? ` (${copies} copies)` : ''}`);
              setHasPrinted(true);
              // Auto-close and return to sales screen
              setTimeout(() => onCloseRef.current(), 300);
              return;
            } else {
              console.warn('QZ Tray print failed, falling back to browser print:', result.error);
              toast.error(`QZ Tray: ${result.error}. Using browser print.`);
            }
          } catch (error) {
            console.warn('QZ Tray error, falling back to browser print:', error);
          }
        }
        
        // Fallback: Browser print with dialog (72mm printable area).
        // Everything is now rendered as ONE physical page: `size: 72mm auto`
        // makes Chromium size the page to the actual content height, and we
        // aggressively force `page-break-*: auto` everywhere so no CSS rule
        // can accidentally split the receipt across two pages (which is what
        // caused the auto-cutter to fire between items and totals in the
        // past).
        const thermalStyles = `
          <style>
            @page { size: 72mm auto; margin: 0mm; }
            * { 
              box-sizing: border-box; 
              margin: 0; 
              padding: 0;
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
            }
            html, body { break-inside: auto !important; page-break-inside: auto !important; }
            body { 
              font-family: 'Courier New', 'Lucida Console', monospace; 
              font-size: 14px; 
              font-weight: 900 !important;
              line-height: 1.4;
              width: 70mm; 
              margin: 0 auto; 
              padding: 1mm;
              color: #000 !important;
              text-transform: uppercase;
              -webkit-font-smoothing: none;
            }
            .receipt { 
              width: 100%; 
              font-weight: 900 !important; 
            }
            /* When two copies are printed (yard stock), we still want an
               explicit break between the two receipts. */
            .copy-separator { page-break-after: always; margin: 8mm 0; padding-top: 4mm; }
            .copy-label { text-align: center; font-weight: 900 !important; font-size: 14px; margin-bottom: 2mm; }
            .text-center { text-align: center; }
            div, span, p { font-weight: 900 !important; color: #000 !important; }
            /* Force EVERY receipt element to allow natural flow — no
               page-break-avoid hints. See qzPrint.js for the full history
               of why this must be auto, not avoid. */
            .receipt, .receipt *, .totals-section, .payment-info, .total-line {
              page-break-inside: auto !important;
              break-inside: auto !important;
              page-break-before: auto !important;
              page-break-after: auto !important;
              break-before: auto !important;
              break-after: auto !important;
            }
            img { width: 100% !important; max-width: 68mm !important; min-height: 20mm; height: auto !important; display: block !important; margin: 0 auto 6mm auto !important; }
            /* Per-copy trailing feed strip. 45 mm covers the worst-case
               thermal auto-cutter offset — the cutter head sits below
               the print head, so this blank strip is what actually
               gets sliced, sparing the last printed line. */
            .receipt-feed-zone { height: 45mm; line-height: 45mm; }
            @media print { 
              * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
              html, body { width: 72mm; margin: 0; padding: 1mm; }
              .receipt, .receipt *, .totals-section, .payment-info, .total-line {
                page-break-inside: auto !important;
                break-inside: auto !important;
                page-break-before: auto !important;
                page-break-after: auto !important;
                break-before: auto !important;
                break-after: auto !important;
              }
              .receipt-feed-zone { height: 45mm; }
            }
          </style>
        `;
        
        const printWindow = window.open('', '_blank', 'width=302,height=600');
        if (!printWindow) {
          toast.error("Pop-up blocked. Please allow pop-ups for printing.");
          return;
        }
        
        printWindow.document.write(`
          <!DOCTYPE html>
          <html>
            <head>
              <title>${transaction.transaction_number || 'Receipt'}</title>
              ${thermalStyles}
            </head>
            <body>
              ${copies === 2 ? '<div class="copy-label">--- COPY 1 (CUSTOMER) ---</div>' : ''}
              ${receiptHtml}
              <!-- Bottom feed zone: without this the thermal auto-cutter
                   (which sits 15-25mm BELOW the print head) slices through
                   the LAST printed line. 35mm covers the worst case. -->
              <div class="receipt-feed-zone">&nbsp;</div>
              ${copies === 2 ? `
                <div class="copy-separator"></div>
                <div class="copy-label">--- COPY 2 (YARD/STORE) ---</div>
                ${receiptHtml}
                <div class="receipt-feed-zone">&nbsp;</div>
              ` : ''}
            </body>
          </html>
        `);
        
        printWindow.document.close();
        printWindow.focus();
        
        // Wait for images to load before printing
        const images = printWindow.document.querySelectorAll('img');
        let loadedCount = 0;
        const totalImages = images.length;
        
        const tryPrint = () => {
          printWindow.print();
          printWindow.close();
          setHasPrinted(true);
          setTimeout(() => onCloseRef.current(), 300);
        };
        
        if (totalImages === 0) {
          setTimeout(tryPrint, 300);
        } else {
          images.forEach((img) => {
            if (img.complete) {
              loadedCount++;
              if (loadedCount === totalImages) setTimeout(tryPrint, 300);
            } else {
              img.onload = () => {
                loadedCount++;
                if (loadedCount === totalImages) setTimeout(tryPrint, 300);
              };
              img.onerror = () => {
                loadedCount++;
                if (loadedCount === totalImages) setTimeout(tryPrint, 300);
              };
            }
          });
          // Fallback after 2 seconds
          setTimeout(() => {
            if (loadedCount < totalImages) tryPrint();
          }, 2000);
        }
      }, 400);
      
      return () => clearTimeout(printTimer);
    }
  }, [open, autoPrint, transaction, companyInfo, companyInfoLoaded, hasPrinted, qzAvailable, copies]);

  const handleSendEmail = async () => {
    if (!emailAddress || !emailAddress.includes("@")) {
      toast.error("Please enter a valid email address");
      return;
    }
    
    setSendingEmail(true);
    try {
      await axios.post(`${API}/email/sale-invoice`, {
        sale_id: transaction.id,
        recipient_email: emailAddress
      });
      toast.success(`Invoice emailed to ${emailAddress}`);
      setShowEmailDialog(false);
    } catch (error) {
      console.error("Error sending email:", error);
      toast.error(error.response?.data?.detail || "Failed to send email");
    } finally {
      setSendingEmail(false);
    }
  };

  if (!transaction) return null;

  const handlePrint = () => {
    const printContent = printRef.current;
    const printWindow = window.open('', '_blank', 'width=302,height=600');
    
    if (!printWindow) {
      toast.error("Pop-up blocked. Please allow pop-ups for printing.");
      return;
    }
    
    const styles = receiptFormat === "thermal" ? `
      <style>
        @page { 
          size: 72mm auto; 
          margin: 0mm;
        }
        * { 
          box-sizing: border-box; 
          margin: 0; 
          padding: 0;
          -webkit-print-color-adjust: exact !important;
          print-color-adjust: exact !important;
        }
        body { 
          font-family: 'Courier New', 'Lucida Console', monospace; 
          font-size: 14px; 
          font-weight: 900 !important;
          line-height: 1.4;
          width: 70mm; 
          margin: 0 auto; 
          padding: 1mm;
          color: #000 !important;
          text-transform: uppercase;
          -webkit-font-smoothing: none;
        }
        .receipt { width: 100%; font-weight: 900 !important; }
        .text-center { text-align: center; }
        .text-right { text-align: right; }
        div, span, p { font-weight: 900 !important; color: #000 !important; }
        /* Keep totals block atomic; do NOT apply to .receipt (would force
           mid-receipt splits before totals — see comment in qzPrint.js). */
        .totals-section, .payment-info, .total-line {
          page-break-inside: avoid !important;
          break-inside: avoid !important;
        }
        .totals-section {
          page-break-before: avoid !important;
          break-before: avoid !important;
        }
        img { 
          width: 100% !important; 
          max-width: 68mm !important; 
          min-height: 20mm;
          height: auto !important; 
          display: block !important; 
          margin: 0 auto 6mm auto !important; 
        }
        @media print { 
          * {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          html, body {
            width: 72mm;
            margin: 0;
            padding: 2mm;
          }
          .totals-section, .payment-info, .total-line {
            page-break-inside: avoid !important;
            break-inside: avoid !important;
          }
          .totals-section {
            page-break-before: avoid !important;
            break-before: avoid !important;
          }
        }
      </style>
    ` : `
      <style>
        @page { size: A4; margin: 15mm; }
        body { 
          font-family: Arial, sans-serif; 
          font-size: 12px; 
          margin: 0; 
          padding: 20px;
        }
        .invoice { max-width: 800px; margin: 0 auto; }
        .header { display: flex; justify-content: space-between; margin-bottom: 30px; }
        .company-info { text-align: left; }
        .invoice-info { text-align: right; }
        table { width: 100%; border-collapse: collapse; margin: 20px 0; }
        th, td { padding: 10px; text-align: left; border-bottom: 1px solid #ddd; }
        th { background-color: #f5f5f5; font-weight: bold; }
        .text-right { text-align: right; }
        .totals { margin-top: 20px; }
        .totals-row { display: flex; justify-content: flex-end; gap: 50px; margin: 5px 0; }
        .grand-total { font-size: 16px; font-weight: bold; border-top: 2px solid #000; padding-top: 10px; }
        .footer { margin-top: 40px; text-align: center; font-size: 11px; color: #666; }
      </style>
    `;

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>${transaction.transaction_number}</title>
          ${styles}
        </head>
        <body>
          ${printContent.innerHTML}
        </body>
      </html>
    `);
    
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 250);
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-ZA', { 
      year: 'numeric', 
      month: '2-digit', 
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const isRefund = transaction.transaction_type?.includes("REFUND");
  const isPettyCash = transaction.transaction_type === "PETTY CASH";
  const isOfflineSale = transaction.is_offline_sale || transaction.pending_sync;

  // Get expense category display name
  const getExpenseCategoryName = (categoryId) => {
    const categories = {
      "stock_purchase": "Stock/Inventory Purchase",
      "supplier_payment": "Supplier Payment",
      "office_supplies": "Office Supplies",
      "repairs_maintenance": "Repairs & Maintenance",
      "casual_employee": "Casual Employee Payment",
      "other": "Other"
    };
    return categories[categoryId] || categoryId || "Uncategorized";
  };

  // NOTE: We no longer take an early-return "silent auto-print" path here.
  // The previous hidden-only render meant that whenever silent printing failed
  // (e.g. QZ Tray dropped offline on the till, or pop-ups were blocked in PWA
  // mode), the cashier saw NO UI and the receipt never closed — they couldn't
  // even reach a manual Print/Close button. We now always render the Dialog
  // so the cashier has a visible fallback. The auto-print effect still fires
  // and the modal auto-closes ~300ms after a successful print, so the
  // happy-path UX is essentially unchanged (a brief flash) while the failure
  // mode is no longer a soft-lock.

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent
        className="max-w-4xl max-h-[90vh] flex flex-col overflow-hidden"
        onClickCapture={handleModalInteraction}
        onKeyDownCapture={handleModalInteraction}
        onScrollCapture={handleModalInteraction}
        onWheelCapture={handleModalInteraction}
        onMouseDownCapture={handleModalInteraction}
        onTouchStartCapture={handleModalInteraction}
      >
        <DialogHeader className="flex-shrink-0">
          <DialogTitle className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span>{isPettyCash ? "Petty Cash Voucher" : isRefund ? "Refund Receipt" : "Sales Invoice"}</span>
              {isOfflineSale && (
                <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium bg-amber-100 text-amber-700 rounded-full">
                  <Clock className="h-3 w-3" />
                  Pending Sync
                </span>
              )}
            </div>
            <div className="flex gap-2">
              <Button
                variant={receiptFormat === "thermal" ? "default" : "outline"}
                size="sm"
                onClick={() => setReceiptFormat("thermal")}
              >
                80mm Thermal
              </Button>
              <Button
                variant={receiptFormat === "a4" ? "default" : "outline"}
                size="sm"
                onClick={() => setReceiptFormat("a4")}
              >
                A4 Invoice
              </Button>
            </div>
          </DialogTitle>
        </DialogHeader>

        {/* Offline Warning Banner */}
        {isOfflineSale && (
          <div className="flex-shrink-0 flex items-center gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg text-amber-800 text-sm">
            <WifiOff className="h-4 w-4" />
            <span>This sale was made offline and is pending synchronization. Receipt ID: {transaction.invoice_number || transaction.id}</span>
          </div>
        )}

        {/* Format Selection & Print Buttons — sticky at top so cashier
            never has to scroll up to close after scanning the preview. */}
        <div className="flex-shrink-0 flex justify-end gap-2 mb-2 pb-2 border-b bg-white">
          {!isPettyCash && (
            <>
              {showEmailDialog ? (
                <div className="flex items-center gap-2 bg-gray-50 p-2 rounded-lg">
                  <Input
                    type="email"
                    placeholder="customer@email.com"
                    value={emailAddress}
                    onChange={(e) => setEmailAddress(e.target.value)}
                    className="w-48"
                  />
                  <Button 
                    onClick={handleSendEmail} 
                    disabled={sendingEmail}
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    {sendingEmail ? <Loader2 className="h-4 w-4 animate-spin" /> : "Send"}
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => setShowEmailDialog(false)}>
                    Cancel
                  </Button>
                </div>
              ) : (
                <Button 
                  variant="outline" 
                  onClick={() => setShowEmailDialog(true)}
                  className="border-blue-300 text-blue-600 hover:bg-blue-50"
                >
                  <Mail className="h-4 w-4 mr-2" />
                  Email Invoice
                </Button>
              )}
            </>
          )}
          <Button onClick={handlePrint} className={isPettyCash ? "bg-green-600 hover:bg-green-700" : "bg-purple-600 hover:bg-purple-700"} data-testid="print-receipt-button">
            <Printer className="h-4 w-4 mr-2" />
            Print {receiptFormat === "thermal" ? "Receipt" : isPettyCash ? "Voucher" : "Invoice"}
          </Button>
          <Button 
            variant="default" 
            onClick={onClose}
            className={autoPrint ? "bg-green-600 hover:bg-green-700" : "bg-slate-700 hover:bg-slate-800"}
            data-testid="close-receipt-button"
          >
            <X className="h-4 w-4 mr-2" />
            {autoPrint ? "Done - Next Sale" : "Skip Print - Next Sale"}
          </Button>
        </div>

        {/* Receipt Preview — scrollable body */}
        <div className={`flex-1 overflow-y-auto border rounded-lg p-4 ${receiptFormat === "thermal" ? "bg-white max-w-[250px] mx-auto" : "bg-white"}`}>
          <div ref={printRef}>
            {receiptFormat === "thermal" ? (
              <ThermalReceipt transaction={liveTransaction || transaction} isRefund={isRefund} isPettyCash={isPettyCash} formatDate={formatDate} getExpenseCategoryName={getExpenseCategoryName} companyInfo={companyInfo} vatEnabled={vatEnabled} />
            ) : (
              <A4Invoice transaction={liveTransaction || transaction} isRefund={isRefund} isPettyCash={isPettyCash} formatDate={formatDate} getExpenseCategoryName={getExpenseCategoryName} companyInfo={companyInfo} vatEnabled={vatEnabled} />
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Helper: determine if invoice is paid.
// Priority 1: if the sale has been allocated to (amount_paid >= total), it's PAID
//             — this covers account invoices that were later settled via account
//             receipt allocations / reconciliations.
// Priority 2: account-type sales with no allocations yet => NOT PAID
// Priority 3: cash / card / loyalty / EFT etc. => PAID
const isInvoicePaid = (transaction) => {
  const total = Number(transaction?.total ?? 0);
  const amountPaid = Number(transaction?.amount_paid ?? 0);
  // Settled via allocations / reconciliation
  if (total > 0 && amountPaid + 0.01 >= total) return true;

  // Fallback: transaction_type indicates account sale
  const txType = (transaction?.transaction_type || "").toString().toUpperCase();
  if (txType.includes("ACCOUNT")) return false;

  const payments = transaction?.payments || [];
  if (payments.length === 0) {
    // No payments recorded yet => treat as not paid
    return false;
  }
  // If ANY payment is an account charge, treat as NOT PAID
  const hasAccount = payments.some((p) => {
    const m = (p?.method || "").toString().toUpperCase();
    return m.includes("ACCOUNT");
  });
  return !hasAccount;
};

// 72mm Thermal Receipt Component - Optimized for 80mm paper with margins
const ThermalReceipt = ({ transaction, isRefund, isPettyCash, formatDate, getExpenseCategoryName, companyInfo, vatEnabled = true }) => {
  // Check if this is a customer transaction (has account number)
  const hasCustomerDetails = transaction.customer_account_number || transaction.customer_name;
  const paid = isInvoicePaid(transaction);
  const showPaidStamp = !isPettyCash && !isRefund;

  const buildCustomerAddress = () => {
    const parts = [];
    if (transaction.customer_address) parts.push(transaction.customer_address);
    if (transaction.customer_suburb) parts.push(transaction.customer_suburb);
    if (transaction.customer_province) parts.push(transaction.customer_province);
    return parts;
  };
  
  // Calculate item count (total quantity)
  const itemCount = transaction.items?.reduce((sum, item) => sum + (item.quantity || 1), 0) || 0;
  
  // Calculate change for cash payments
  const cashPayment = transaction.payments?.find(p => p.method === "CASH");
  const tendered = cashPayment?.amount || 0;
  const change = cashPayment ? tendered - transaction.total : 0;
  
  return (
    <div className="receipt font-mono uppercase" style={{ width: '70mm', fontWeight: 'bold', color: '#000' }}>
      {/* Logo - FULL WIDTH */}
      <div className="text-center" style={{ marginBottom: '6mm' }}>
        <img 
          src={companyInfo.logo} 
          alt={companyInfo.name}
          style={{ width: '100%', maxWidth: '68mm', height: 'auto', minHeight: '20mm', objectFit: 'contain', display: 'block', margin: '0 auto' }}
        />
      </div>

      {/* Company Header - Larger text */}
      <div className="text-center" style={{ marginBottom: '5mm', fontSize: '16px', fontWeight: 'bold' }}>
        <div style={{ fontSize: '18px', fontWeight: '900' }}>{companyInfo.name}</div>
        <div>{companyInfo.address}</div>
        {companyInfo.addressLine2 && <div>{companyInfo.addressLine2}</div>}
        <div>{[companyInfo.city, companyInfo.postalCode].filter(Boolean).join(" ")}</div>
        <div>TEL: {companyInfo.phone}</div>
        {companyInfo.storeNumber && <div>STORE: {companyInfo.storeNumber}</div>}
        {vatEnabled && companyInfo.vatNumber && <div>{companyInfo.vatNumber}</div>}
        {companyInfo.registrationNumber && <div>{companyInfo.registrationNumber}</div>}
      </div>

      {/* Spacer */}
      <div style={{ height: '5mm' }}></div>

      {/* Invoice Number - Large and bold */}
      <div className="text-center" style={{ fontSize: '18px', fontWeight: '900', marginBottom: '3mm' }}>
        {isPettyCash ? "PETTY CASH" : isRefund ? "REFUND" : (vatEnabled ? "TAX INVOICE" : "INVOICE")} NO: {transaction.invoice_number || transaction.transaction_number?.replace(/[A-Z]+-/, '') || transaction.transaction_number}
      </div>

      {/* PAID / NOT PAID stamp removed per user request (all invoices, all formats) */}

      {/* Split Invoice Label - Show if this is a split invoice */}
      {transaction.split_invoice_label && (
        <div className="text-center" style={{ fontSize: '14px', fontWeight: '900', marginBottom: '3mm', padding: '2mm', border: '2px solid black' }}>
          ** {transaction.split_invoice_label} **
        </div>
      )}

      {/* Spacer */}
      <div style={{ height: '4mm' }}></div>

      {/* Transaction Info */}
      <div style={{ fontSize: '14px', fontWeight: 'bold' }}>
        <div>Date: {formatDate(transaction.created_at)}</div>
        <div>Sales Person: {transaction.cashier || "Cashier 1"}</div>
      </div>

      {/* Customer Details Section - Show if customer account */}
      {hasCustomerDetails && !isPettyCash && (
        <>
          <div style={{ height: '4mm' }}></div>
          <div style={{ fontSize: '14px', fontWeight: 'bold' }}>
            <div style={{ fontWeight: '900' }}>CUSTOMER:</div>
            <div>{transaction.customer_name}</div>
            {transaction.customer_account_number && (
              <div>A/C: {transaction.customer_account_number}</div>
            )}
            {buildCustomerAddress().map((line, idx) => (
              <div key={idx}>{line}</div>
            ))}
            {transaction.customer_contact && (
              <div>TEL: {transaction.customer_contact}</div>
            )}
            {transaction.customer_vat_number && (
              <div>VAT: {transaction.customer_vat_number}</div>
            )}
            {transaction.customer_order_number && (
              <div style={{ fontWeight: '900' }}>ORDER NO: {transaction.customer_order_number}</div>
            )}
          </div>
        </>
      )}

      {/* Spacer */}
      <div style={{ height: '5mm' }}></div>

      {/* Items */}
      <div style={{ fontSize: '14px', fontWeight: 'bold' }}>
        {transaction.items?.map((item, index) => (
          <div key={index} style={{ marginBottom: '3mm' }}>
            <div style={{ fontWeight: '900' }}>{item.name}</div>
            {item.is_direct_item && item.plu_code && <div style={{ fontSize: '10px', fontWeight: 'normal' }}>{item.plu_code}</div>}
            {item.is_loan && item.loan_principal ? (
              <div style={{ fontSize: '12px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span>Loan Principal:</span>
                  <span>R{item.loan_principal?.toFixed(2)}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span>Interest ({item.loan_interest_rate || 20}%):</span>
                  <span>R{item.loan_interest?.toFixed(2)}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: '900' }}>
                  <span>Total:</span>
                  <span>R{item.total?.toFixed(2)}</span>
                </div>
              </div>
            ) : (
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span>
                  {item.quantity} x {item.unit_price?.toFixed(2)}
                  {item.vat_exemption_applied && (
                    <span style={{ marginLeft: '4px', fontSize: '11px', fontWeight: 'normal' }}>(0% VAT — exempt)</span>
                  )}
                </span>
                <span>{item.total?.toFixed(2)}</span>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Totals Section — no page-break hints. See qzPrint.js for why we
          force auto flow everywhere (fixes cutter firing between items and
          totals when the CSS/browser decides to split "pages"). */}
      <div className="totals-section" style={{ pageBreakInside: 'auto', breakInside: 'auto' }}>
        {/* Spacer */}
        <div style={{ height: '5mm' }}></div>

        {/* VAT breakdown — SARS requires a tax invoice to show subtotal excl,
            VAT amount and total incl separately. Rendered ABOVE the big Total
            line so the customer can see the split at a glance. */}
        {vatEnabled && !isPettyCash && (typeof transaction.tax === "number" ? transaction.tax : 0) > 0 && (
          <div style={{ fontSize: '15px', fontWeight: 'bold', marginBottom: '2mm' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span>Subtotal (Excl VAT):</span>
              <span>R{(transaction.subtotal ?? 0).toFixed(2)}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span>VAT (15%):</span>
              <span>R{(transaction.tax ?? 0).toFixed(2)}</span>
            </div>
          </div>
        )}

        {/* Total - Extra bold and large */}
        <div className="total-line" style={{ display: 'flex', justifyContent: 'space-between', fontSize: '20px', fontWeight: '900' }}>
          <span>{vatEnabled && !isPettyCash ? "Total (Incl VAT):" : "Total:"}</span>
          <span>R{transaction.total?.toFixed(2)}</span>
        </div>

        {/* Spacer */}
        <div style={{ height: '4mm' }}></div>

        {/* Payment Info */}
        <div className="payment-info" style={{ fontSize: '16px', fontWeight: 'bold' }}>
          {transaction.payments?.map((payment, index) => (
            <div key={index}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span>{payment.method === "GIFT_VOUCHER" ? "GIFT VOUCHER" : payment.method}:</span>
                <span>{payment.amount?.toFixed(2)}</span>
              </div>
              {payment.method === "GIFT_VOUCHER" && payment.reference && (
                <div style={{ fontSize: '12px' }}>Voucher {payment.reference}</div>
              )}
              {payment.cash_change > 0 && (
                <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: '900' }}>
                  <span>Voucher change:</span>
                  <span>{payment.cash_change.toFixed(2)}</span>
                </div>
              )}
            </div>
          ))}
          {cashPayment && change > 0 && (
            <>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span>Tendered:</span>
                <span>{tendered.toFixed(2)}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: '900' }}>
                <span>Change:</span>
                <span>{change.toFixed(2)}</span>
              </div>
            </>
          )}
        </div>

        {/* Item Count - only for non-petty cash */}
        {!isPettyCash && (
          <div style={{ marginTop: '3mm', fontSize: '14px', fontWeight: 'bold' }}>
            <div>Item Count: {itemCount}</div>
          </div>
        )}

        {/* Petty Cash Description - Show for petty cash transactions */}
        {isPettyCash && transaction.petty_cash_description && (
          <>
            <div style={{ height: '3mm' }}></div>
            <div style={{ fontSize: '14px', fontWeight: 'bold' }}>
              <div style={{ fontWeight: '900', marginBottom: '2mm' }}>DESCRIPTION:</div>
              <div style={{ whiteSpace: 'pre-wrap' }}>{transaction.petty_cash_description}</div>
            </div>
          </>
        )}
        
        {/* Split Invoice Note - Show if this is part of a split invoice */}
        {transaction.split_note && (
          <>
            <div style={{ height: '3mm' }}></div>
            <div style={{ fontSize: '12px', textAlign: 'center', border: '1px dashed black', padding: '2mm' }}>
              {transaction.split_note}
            </div>
          </>
        )}

        {/* Spacer */}
        <div style={{ height: '5mm' }}></div>

        {/* Footer - Custom notes from Company Settings */}
        <div className="text-center" style={{ fontSize: '14px', fontWeight: '900' }}>
          {companyInfo.footerLine1 && <div>{companyInfo.footerLine1}</div>}
          {companyInfo.footerLine2 && <div>{companyInfo.footerLine2}</div>}
          {companyInfo.footerLine3 && <div style={{ marginTop: '2mm' }}>{companyInfo.footerLine3}</div>}
          {companyInfo.footerLine4 && <div style={{ marginTop: '2mm', fontSize: '12px' }}>{companyInfo.footerLine4}</div>}
          {companyInfo.footerLine5 && <div style={{ fontSize: '12px' }}>{companyInfo.footerLine5}</div>}
        </div>
        
        {/* Extra padding to ensure paper feeds past the cutter before cut.
             Keep this generous (30mm) — some thermal cutters trigger 20-25mm
             above the cut point and will otherwise clip the TOTAL line. */}
        <div style={{ height: '30mm' }}></div>
      </div>
    </div>
  );
};

// A4 Invoice Component
const A4Invoice = ({ transaction, isRefund, isPettyCash, formatDate, getExpenseCategoryName, companyInfo, vatEnabled = true }) => {
  // Check if this is a customer transaction (has account number)
  const hasCustomerDetails = transaction.customer_account_number || transaction.customer_name;
  const paid = isInvoicePaid(transaction);
  const showPaidStamp = !isPettyCash && !isRefund;
  
  // Build customer address string
  const buildCustomerAddress = () => {
    const parts = [];
    if (transaction.customer_address) parts.push(transaction.customer_address);
    if (transaction.customer_suburb) parts.push(transaction.customer_suburb);
    if (transaction.customer_province) parts.push(transaction.customer_province);
    return parts;
  };

  return (
    <div className="invoice" style={{ minHeight: '800px' }}>
      {/* Header with Logo */}
      <div className="flex justify-between mb-8 pb-4 border-b-2 border-gray-300">
        <div className="company-info">
          <img 
            src={companyInfo.logo} 
            alt={companyInfo.name}
            style={{ maxWidth: '200px', maxHeight: '80px', objectFit: 'contain' }}
            className="mb-2"
          />
          <p className="text-gray-600">{companyInfo.address}</p>
          {companyInfo.addressLine2 && <p className="text-gray-600">{companyInfo.addressLine2}</p>}
          <p className="text-gray-600">{[companyInfo.city, companyInfo.postalCode].filter(Boolean).join(" ")}</p>
          <p className="text-gray-600">Tel: {companyInfo.phone}</p>
          {companyInfo.storeNumber && (
            <p className="text-gray-600">Store: {companyInfo.storeNumber}</p>
          )}
          {vatEnabled && companyInfo.vatNumber && (
            <p className="text-gray-600 font-semibold">{companyInfo.vatNumber}</p>
          )}
          {companyInfo.registrationNumber && (
            <p className="text-gray-600">{companyInfo.registrationNumber}</p>
          )}
          <p className="text-gray-600">{companyInfo.email}</p>
        </div>
        <div className="text-right">
          <h2 className={`text-3xl font-bold ${isPettyCash ? "text-green-600" : isRefund ? "text-orange-600" : "text-purple-600"}`}>
            {isPettyCash ? "PETTY CASH VOUCHER" : isRefund ? "CREDIT NOTE" : vatEnabled ? "TAX INVOICE" : "INVOICE"}
          </h2>
          <p className="text-lg font-semibold mt-2">{transaction.transaction_number}</p>
          <p className="text-gray-600 mt-4">Date: {formatDate(transaction.created_at)}</p>
          <p className="text-gray-600">Cashier: {transaction.cashier || "Cashier 1"}</p>
          {/* PAID / NOT PAID stamp removed per user request */}
        </div>
      </div>

      {/* Petty Cash Expense Details */}
      {isPettyCash && (
        <div className="mb-6 p-4 bg-green-50 rounded border border-green-200">
          <h3 className="font-bold text-green-800 mb-3 text-lg">Expense Details</h3>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <span className="text-gray-600">Category: </span>
              <span className="font-semibold">{getExpenseCategoryName(transaction.expense_category)}</span>
            </div>
          </div>
          {transaction.petty_cash_description && (
            <div className="mt-3">
              <span className="text-gray-600">Description: </span>
              <p className="font-medium mt-1 whitespace-pre-wrap">{transaction.petty_cash_description}</p>
            </div>
          )}
        </div>
      )}

      {/* Customer Info - Enhanced with full details */}
      {hasCustomerDetails && !isPettyCash && (
        <div className="mb-6 p-4 bg-gray-50 rounded border border-gray-200">
          <div className="grid grid-cols-2 gap-8">
            <div>
              <h3 className="font-bold text-gray-700 mb-2 text-lg">Bill To:</h3>
              <p className="font-semibold text-lg">{transaction.customer_name}</p>
              {buildCustomerAddress().map((line, idx) => (
                <p key={idx} className="text-gray-600">{line}</p>
              ))}
              {transaction.customer_contact && (
                <p className="text-gray-600 mt-1">Tel: {transaction.customer_contact}</p>
              )}
            </div>
            <div className="text-right">
              {transaction.customer_account_number && (
                <div className="mb-2">
                  <span className="text-gray-600">Account No: </span>
                  <span className="font-semibold text-lg">{transaction.customer_account_number}</span>
                </div>
              )}
              {transaction.order_number && (
                <div className="mb-2 p-2 bg-amber-100 rounded border border-amber-300">
                  <span className="text-amber-800 font-medium">Ref/Order No: </span>
                  <span className="font-bold text-amber-900 text-lg">{transaction.order_number}</span>
                </div>
              )}
              {transaction.customer_vat_number && (
                <div>
                  <span className="text-gray-600">VAT No: </span>
                  <span className="font-semibold">{transaction.customer_vat_number}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Items Table */}
      <table className="w-full mb-6">
        <thead>
          <tr className="bg-gray-100">
            <th className="text-left p-3 border-b-2">Description</th>
            <th className="text-right p-3 border-b-2">Qty</th>
            <th className="text-right p-3 border-b-2">Unit Price</th>
            <th className="text-right p-3 border-b-2">Amount</th>
          </tr>
        </thead>
        <tbody>
          {transaction.items?.map((item, index) => (
            <tr key={index} className="border-b">
              <td className="p-3">
                {item.name}
                {item.is_direct_item && item.plu_code && <div className="text-[10px] text-gray-500">{item.plu_code}</div>}
                {item.is_loan && item.loan_principal ? (
                  <div className="text-xs text-gray-500 mt-0.5">
                    Principal: R {item.loan_principal?.toFixed(2)} + Interest ({item.loan_interest_rate || 20}%): R {item.loan_interest?.toFixed(2)}
                  </div>
                ) : null}
                {item.vat_exemption_applied && (
                  <div className="text-xs text-gray-500 mt-0.5">0% VAT — customer exemption applied</div>
                )}
              </td>
              <td className="p-3 text-right">{item.quantity}</td>
              <td className="p-3 text-right">R {item.unit_price?.toFixed(2)}</td>
              <td className="p-3 text-right">R {item.total?.toFixed(2)}</td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* Totals */}
      <div className="flex justify-end">
        <div className="w-64">
          {vatEnabled && (typeof transaction.tax === "number" ? transaction.tax : 0) > 0 ? (
            <>
              <div className="flex justify-between py-2 border-b">
                <span className="text-gray-600">Subtotal (Excl VAT):</span>
                <span>R {transaction.subtotal?.toFixed(2)}</span>
              </div>
              <div className="flex justify-between py-2 border-b">
                <span className="text-gray-600">VAT (15%):</span>
                <span>R {transaction.tax?.toFixed(2)}</span>
              </div>
            </>
          ) : (
            <div className="flex justify-between py-2 border-b">
              <span className="text-gray-600">Subtotal:</span>
              <span>R {transaction.total?.toFixed(2)}</span>
            </div>
          )}
          <div className="flex justify-between py-3 text-xl font-bold border-t-2 border-black mt-2">
            <span>TOTAL:</span>
            <span className={isRefund ? "text-orange-600" : ""}>R {transaction.total?.toFixed(2)}</span>
          </div>
        </div>
      </div>

      {/* Payment Info */}
      <div className="mt-8 p-4 bg-gray-50 rounded">
        <h3 className="font-bold text-gray-700 mb-2">Payment Details:</h3>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <span className="text-gray-600">Payment Method:</span>
            <span className="ml-2 font-semibold">
              {transaction.payments?.map(p => p.method).join(", ")}
            </span>
          </div>
          <div>
            <span className="text-gray-600">Transaction Type:</span>
            <span className="ml-2 font-semibold">{transaction.transaction_type}</span>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="mt-12 pt-4 border-t text-center text-gray-500 text-sm">
        <p>Thank you for your business!</p>
        <p className="mt-2">Goods received in good order. No refunds without original receipt.</p>
        <p className="mt-4">
          {[companyInfo.name, companyInfo.phone, companyInfo.storeNumber && `Store: ${companyInfo.storeNumber}`, companyInfo.email]
            .filter(Boolean).join(" | ")}
        </p>
      </div>
    </div>
  );
};

export default ReceiptModal;
